import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { format, parseISO } from 'date-fns';
import { isArray, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, DateTime, Autocomplete, Combobox } from '~/components/Form';
import {
  LOCALIDADE_ABASTECIMENTO,
  MOTORISTA_ABASTECIMENTO,
} from '~/constants/modules';
import { can, t, validCharacters } from '~/helpers';
import api from '~/services/api';

import * as S from './styles';

export default function CreateOrUpdate({
  data,
  sending,
  onForm,
  toggleDisabled,
}) {
  const formRef = useRef(null);
  const toggleDisabledRef = useRef(toggleDisabled);
  const onFormRef = useRef(onForm);

  const [posto, setPosto] = useState(0);
  const [localidadeFixa, setLocalidadeFixa] = useState(null);
  const [motorista, setMotorista] = useState(null);
  const [justification, setJustification] = useState(false);
  const [loadingScreen, setLoadingScreen] = useState(false);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const loadingErrorMessage = 's.geral.loadingerror';

  const { client } = useSelector((state) => state.user.profile);

  const { formaidfrentistaabast } = useSelector((state) => state.user.configs);

  const [exibemotoristaterminal, setExibemotoristaterminal] = useState(0);
  const [
    exibemotoristatransferenciaterminal,
    setExibemotoristatransferenciaterminal,
  ] = useState(0);

  const handleAsyncLoadingChange = useCallback(
    (requestLoading) => {
      setPendingRequests((state) => {
        if (hasLoadingError) return 0;
        if (requestLoading) return state + 1;
        return state > 0 ? state - 1 : 0;
      });
    },
    [hasLoadingError]
  );

  const handleAsyncLoadingError = useCallback(() => {
    setHasLoadingError(true);
    setPendingRequests(0);
  }, []);

  const handleTrackApiRequest = useCallback(
    async (request) => {
      handleAsyncLoadingChange(true);

      try {
        return await request();
      } catch (error) {
        handleAsyncLoadingError();
        throw error;
      } finally {
        handleAsyncLoadingChange(false);
      }
    },
    [handleAsyncLoadingChange, handleAsyncLoadingError]
  );

  const handleValidateConductor = useCallback(async () => {
    if (!motorista) return;

    await api
      .get(`motoristas/${motorista}`, {
        params: { pesquisalista: true },
      })
      .catch(async () => {
        formRef.current !== null &&
          formRef.current.setFieldValue('idmotorista', '');
        formRef.current !== null &&
          formRef.current.setFieldError(
            'idmotorista',
            'Motorista não encontrado!'
          );
      });
  }, [motorista]);

  const handleResponseAPIJustification = useCallback(async () => {
    try {
      const response = await handleTrackApiRequest(() =>
        api.get('/justificativascontingencias', {
          params: {
            pagesize: 99999,
            pesquisalista: true,
            wherefield: ['statusjustificativacontingencia'],
            valuewherefield: ['L'],
            typewherefield: ['EQUALS'],
          },
          paramsSerializer: (value) => qs.stringify(value),
        })
      );

      setJustification(!isEmpty(response.data.items));
    } catch (err) {
      setJustification(false);
    }
  }, [handleTrackApiRequest]);

  const handleSubmit = useCallback(
    async (formData) => {
      setLoadingScreen(true);
      toggleDisabledRef.current(true);

      try {
        if (hasLoadingError) {
          toast.error(t(loadingErrorMessage));
          return;
        }

        if (pendingRequests > 0) {
          toast.warning(t('s.geral.validacao.aguardecarregamento'));
          return;
        }

        const schema = Yup.object().shape({
          idposto: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          idbico: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          idpostodestinomovimentacaotransferencia: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.campoobrigatorio')),
          encerrantemovimentacaotransferencia: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.campoobrigatorio')),
          encerranteinicialmovimentacao: Yup.number().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          encerrantefinalmovimentacao: Yup.number().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          volumemovimentacao: Yup.number().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          codigousuario:
            formaidfrentistaabast !== 0
              ? Yup.string()
                  .test(
                    'characters-invalid',
                    t('s.configuracao.geral.naopodecontercaracterespecial'),
                    (value) => validCharacters(value)
                  )
                  .required(t('s.geral.validacao.campoobrigatorio'))
                  .nullable()
              : Yup.string(),
          idmotorista:
            can(MOTORISTA_ABASTECIMENTO) && exibemotoristaterminal > 0
              ? Yup.string()
                  .required(t('s.geral.validacao.campoobrigatorio'))
                  .typeError(t('s.geral.validacao.campoobrigatorio'))
              : Yup.string(),
          idlocalidade: can(LOCALIDADE_ABASTECIMENTO)
            ? Yup.number()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string(),
          idjustificativacontingencia: justification
            ? Yup.string()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string().nullable(),
        });

        await schema.validate(formData, {
          abortEarly: false,
        });

        const modifyItems = { ...data };

        delete modifyItems.codigoauxiliarposto;
        delete modifyItems.nomeposto;
        delete modifyItems.codigoposto;
        delete modifyItems.altitudegeolocalizacaocomboio;
        delete modifyItems.codigoauxiliarproduto;
        delete modifyItems.dentrocercageolocalizacaocomboio;
        delete modifyItems.emailusuario;
        delete modifyItems.formaidmovimentacaostr;
        delete modifyItems.latitudegeolocalizacaocomboio;
        delete modifyItems.longitudegeolocalizacaocomboio;
        delete modifyItems.modomovimentacaostr;
        delete modifyItems.nomeabreviadoposto;
        delete modifyItems.nomeabreviadoproduto;
        delete modifyItems.nomeproduto;
        delete modifyItems.nomeusuario;
        delete modifyItems.precototalmovimentacao;
        delete modifyItems.rownum;
        delete modifyItems.tipomovimentacaostr;
        delete modifyItems.tipoproduto;
        delete modifyItems.codigonomeposto;
        delete modifyItems.modoidentificadousuariostr;
        delete modifyItems.idpostousuariologado;
        delete modifyItems.idclienteusuariologado;

        delete modifyItems.motivomovimentacao;

        const formDataSend = {
          contingenciamovimentacaotransferencia: null,
          dataintegracaomovimentacao: null,
          encerranteanteriormovimentacaotransferencia: 0,
          gerainterfacemovimentacao: '',
          volumepredeterminadomovimentacao: 0.0,
          modomovimentacao: 0,
          formaidmovimentacao: 0,
          numerologmovimentacao: 0,
          onlinemovimentacao: 0,
          vazaomediamovimentacao: 0,
          vazaomaximamovimentacao: 0,
          codigousuario: '',
          ...modifyItems,
          datarecebimentomovimentacao: format(
            parseISO(modifyItems.datarecebimentomovimentacao),
            'dd/MM/yyyy HH:mm:ss'
          ),
          ...formData,
          idcliente: client,
          idlocalidade: can(LOCALIDADE_ABASTECIMENTO)
            ? Number(formData.idlocalidade) || null
            : null,
          idjustificativacontingencia: justification
            ? formData.idjustificativacontingencia
            : null,
        };

        await api.put('transformacoesvazoesemtransferencias', formDataSend);
        toast.success(t('s.geral.sucessoalteracao'));
        await onFormRef.current();
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message;
          });

          formRef.current.setErrors(validationErrors);
        } else if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      } finally {
        setLoadingScreen(false);
        toggleDisabledRef.current(false);
      }
    },
    [
      client,
      data,
      formaidfrentistaabast,
      hasLoadingError,
      justification,
      pendingRequests,
      loadingErrorMessage,
    ]
  );

  useEffect(() => {
    if (sending) formRef.current.submitForm();
  }, [sending]);

  useEffect(() => {
    toggleDisabledRef.current = toggleDisabled;
  }, [toggleDisabled]);

  useEffect(() => {
    onFormRef.current = onForm;
  }, [onForm]);

  useEffect(() => {
    handleResponseAPIJustification();
  }, [handleResponseAPIJustification]);

  useEffect(() => {
    toggleDisabledRef.current(
      loadingScreen || pendingRequests > 0 || hasLoadingError
    );
  }, [hasLoadingError, loadingScreen, pendingRequests]);

  useEffect(
    () => () => {
      toggleDisabledRef.current(false);
    },
    []
  );

  if (hasLoadingError) {
    return (
      <div
        style={{
          border: '1px solid #f5c6cb',
          background: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px',
          padding: '16px',
          lineHeight: 1.5,
        }}
      >
        {t(loadingErrorMessage)}
      </div>
    );
  }

  if (!isEmpty(data))
    return (
      <>
        <S.ContainerForm ref={formRef} initialData={data} onSubmit={handleSubmit}>
          <Autocomplete
            disabled
            name="idposto"
            label="nomeposto"
            url="postoscomboios"
            unique="idposto"
            title={t('s.menu.configuracao.posto')}
            getValue={({ idposto, idlocalidade }) => {
              setPosto(idposto || 0);
              setLocalidadeFixa(
                Number(idlocalidade) > 0 ? Number(idlocalidade) : null
              );
            }}
            onLoadingChange={handleAsyncLoadingChange}
            onLoadingError={handleAsyncLoadingError}
          />
          <Combobox
            disabled
            name="idbico"
            label="bicoprodutobico"
            url="bicos"
            unique="idbico"
            title={t('s.menu.configuracao.bico')}
            hiddens={[{ name: 'idterminal' }, { name: 'idproduto' }]}
            params={{
              wherefield: 'idposto',
              valuewherefield: posto || data.idposto,
              typewherefield: 'EQUALS',
              fieldlist:
                'idbico, idterminal, idproduto, bicoprodutobico, tipoproduto, exibemotoristaterminal, exibemotoristatransferenciaterminal',
            }}
            getValue={(value) => {
              if (value?.exibemotoristaterminal) {
                setExibemotoristaterminal(
                  value?.exibemotoristatransferenciaterminal
                );
                setExibemotoristatransferenciaterminal(
                  value?.exibemotoristaterminal
                );
              } else {
                setExibemotoristatransferenciaterminal(0);
                setExibemotoristaterminal(0);
              }
            }}
            onLoadingChange={handleAsyncLoadingChange}
            onLoadingError={handleAsyncLoadingError}
          />

          <DateTime
            disabled
            time
            name="datahorainicialmovimentacao"
            label={t('s.operacao.abastecimento.datahorainicialmovimentacao')}
          />

          <DateTime
            disabled
            time
            name="datahorafinalmovimentacao"
            label={t('s.operacao.abastecimento.datahorafinalmovimentacao')}
          />

          <Autocomplete
            name="idpostodestinomovimentacaotransferencia"
            label="codigonomeposto"
            url="postoscomboios"
            unique="idposto"
            title={t('s.geral.postocomboio')}
            onLoadingChange={handleAsyncLoadingChange}
            onLoadingError={handleAsyncLoadingError}
          />

          <Input
            name="encerrantemovimentacaotransferencia"
            type="number"
            label={t(
              's.operacao.transferencia.encerrantemovimentacaotransferencia'
            )}
          />

          <Input
            disabled
            name="encerranteinicialmovimentacao"
            label={t('s.operacao.abastecimento.encerranteinicialmovimentacao')}
            type="number"
          />

          <Input
            disabled
            name="encerrantefinalmovimentacao"
            label={t('s.operacao.abastecimento.encerrantefinalmovimentacao')}
            type="number"
          />

          <Input
            disabled
            name="volumemovimentacao"
            label={t('s.operacao.abastecimento.volumemovimentacao')}
            type="number"
          />

          {can(LOCALIDADE_ABASTECIMENTO) && (
            <Autocomplete
              name="idlocalidade"
              label="nomelocalidade"
              url="localidades"
              unique="idlocalidade"
              title="Localidade"
              disabled={Number(localidadeFixa) > 0}
              forcedValue={
                Number(localidadeFixa) > 0 ? localidadeFixa : undefined
              }
              onLoadingChange={handleAsyncLoadingChange}
              onLoadingError={handleAsyncLoadingError}
            />
          )}

          {can(MOTORISTA_ABASTECIMENTO) &&
            exibemotoristatransferenciaterminal > 0 && (
              <Input
                name="idmotorista"
                label={t('s.operacao.abastecimento.idmotorista')}
                getValue={(value) => setMotorista(value)}
                onBlur={handleValidateConductor}
                onKeyUp={({ key }) =>
                  key === 'Enter' && handleValidateConductor()
                }
              />
            )}

          {formaidfrentistaabast !== 0 && (
            <Input
              name="codigousuario"
              label={t('s.operacao.abastecimento.codigousuario')}
            />
          )}

          {justification && (
            <Autocomplete
              name="idjustificativacontingencia"
              url="justificativascontingencias"
              id="idjustificativacontingencia"
              label="descricaojustificativacontingencia"
              unique="idjustificativacontingencia"
              title={t('s.menu.operacao.justificativacontingencia')}
              params={{
                pagesize: 99999,
                pesquisalista: true,
                wherefield: ['statusjustificativacontingencia'],
                valuewherefield: ['L'],
                typewherefield: ['EQUALS'],
              }}
              onLoadingChange={handleAsyncLoadingChange}
              onLoadingError={handleAsyncLoadingError}
            />
          )}
        </S.ContainerForm>
        <Spinner loading={loadingScreen || pendingRequests > 0} />
      </>
    );

  return <div />;
}

CreateOrUpdate.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  sending: PropTypes.bool.isRequired,
  onForm: PropTypes.func.isRequired,
  toggleDisabled: PropTypes.func.isRequired,
};
